import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import { DetailHero } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalHeroIntro } from './ProfessionalHeroIntro';
import { ProfessionalHeroCaption } from './ProfessionalHeroCaption';
import { ProfileFamilyMarks } from './ProfileFamilyMarks';
import { getSelectedShowcase } from '../utils/featured-work';
import { ScrollCue } from '~/ui-rocco/pages/detail-shared';

export const ProfessionalHeroSection = () => {
    // Preview-aware: resolves the layout payload under both the public route
    // and the Business Manager preview, with any BM edit patch applied.
    const layoutData = useProfessionalLayoutData();
    const family = useProfileFamily();
    if (!layoutData) return null;
    const { initial, banners, membershipLine } = layoutData;
    const discipline = initial.PrimaryCategory?.PrettyTitle;
    // No figures under the discipline: the profile's views and saves are
    // stated once, on the tab line under the banner.
    const audience: never[] = [];
    // The cities they work from, deduped and in the order the CMS lists them.
    const cities = Array.from(
        new Set(
            (initial.LibraryLocations ?? [])
                .map((location) => location?.City?.trim())
                .filter((city): city is string => Boolean(city)),
        ),
    ).join(', ');

    const images = banners
        .map((banner) => banner.libraryImageFile?.DetailBannerStandard?.Desktop)
        .filter((src): src is string => Boolean(src));

    // What the intro flicks through: their featured work first, then the
    // banners, ending on the banner the page settles to.
    //
    // The work is the point — a reel of the same handful of banner shots says
    // far less about a practice than its projects do, and a supplier's
    // products less still. `getSelectedShowcase` is the rule the Featured work
    // grid already follows: products for manufacturers, projects for everyone
    // else. The banners follow it so a thin profile still has enough frames to
    // read as a reel.
    const showcase = getSelectedShowcase(
        layoutData.overviewShared?.featuredProjects?.featuredProjectsList,
        layoutData.overviewShared?.featuredProducts?.featuredProductsList,
        Boolean(
            layoutData.overviewShared?.professionalOverview?.IsManufacturer,
        ),
    );
    const featuredImages = showcase.items
        .map((item) => item.images?.[0]?.src)
        .filter((src): src is string => Boolean(src));

    // Thumbnails, not the banner renditions: the reel shows each frame for a
    // seventh of a second at a fraction of the screen, so a 1920px photograph
    // buys it nothing and costs it everything — decoding several at once
    // stalled the main thread enough that the sequence's own transitions
    // visibly stuttered.
    const bannerThumbs = banners
        .map(
            (banner) =>
                banner.libraryImageFile?.ThumbLandscapeDesktop ??
                banner.libraryImageFile?.ThumbLandscapeMobile ??
                banner.libraryImageFile?.DetailBannerStandard?.Desktop,
        )
        .filter((src): src is string => Boolean(src));

    const reelImages = [...featuredImages, ...bannerThumbs];

    // Small first-banner rendition as the hero's base layer: the full-size
    // banner is the page's largest asset, so the hero paints near-instantly
    // and sharpens rather than sitting on the dark slab.
    const firstBannerFile = banners[0]?.libraryImageFile;
    const heroBaseSrc =
        firstBannerFile?.ThumbLandscapeDesktop ??
        firstBannerFile?.ThumbLandscapeMobile ??
        null;

    return (
        <DetailHero
            intro={
                // Keyed on the profile so it remounts per professional. Moving
                // between two profiles is a client-side navigation into the
                // same component in the same position, so without a key React
                // keeps the instance — and with it the state saying the intro
                // has already finished, which leaves the new page with no
                // opening at all.
                <ProfessionalHeroIntro
                    key={initial.ID}
                    name={initial.Title}
                    images={reelImages}
                    finalImage={images[0]}
                    graded
                    discipline={discipline ?? undefined}
                    audience={audience}
                />
            }
            scrollCue={<ScrollCue />}
            title={initial.Title}
            belowTitle={
                <ProfessionalHeroCaption
                    discipline={discipline ?? undefined}
                    audience={audience}
                />
            }
            backgroundImages={images}
            parallax
            tameHighlights
            // A flat 10% black over the photograph, inside the graded box so it
            // is part of the picture rather than a wash laid over the finished
            // one. The intro's last frame carries the same, or the handover
            // would show the banner a shade darker than the frame it replaced.
            tintClassName="absolute inset-0 bg-black/10"
            // Gestura Headline, the editorial serif the articles are set in.
            // Set tight, almost touching: Gestura is drawn with generous
            // sidebearings and a name at 120px needs them closed up to read as
            // one word rather than a row of letters.
            // `mt-0` matters: the title carries a 14px top margin meant to
            // space it under an eyebrow, and with the block centred and no
            // eyebrow above it that margin pushes the name half of it below
            // the centre — a 7px settle exactly as the intro hands over.
            titleClassName="font-gestura font-normal md:mt-0 md:tracking-[-0.045em]"
            // Centred on the banner, which is exactly where the intro's last
            // frame leaves the name. The two have to agree: the intro fades
            // out over the real title, so any difference in position or size
            // reads as the name jumping at the very end of the sequence.
            // Centred on the VIEWPORT, not on the banner. The banner is
            // taller than the window, so its own midpoint sits well below the
            // eye line on load and the name read as hanging low.
            contentClassName="md:top-[50vh] md:bottom-auto md:left-1/2 md:w-[90%] md:max-w-[68rem] md:-translate-x-1/2 md:-translate-y-1/2 md:text-center"
            heroBaseSrc={heroBaseSrc}
            // Figma 5498:66909 — the family marks, sized and overlapped as
            // ratios of the brand badge they used to fan out from. With that
            // badge moved down into the summary block they take its place in
            // the banner's right gutter rather than trailing off nothing.
            logoAside={
                family ? (
                    <ProfileFamilyMarks
                        brands={family.others}
                        className="absolute z-10 -space-x-[0.761rem] max-md:hidden md:right-[5.52rem] md:-bottom-[1.422rem]"
                        markClassName="md:size-[5.494rem]"
                    />
                ) : null
            }
        />
    );
};
